var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import * as jexl from 'jexl';
import * as objectPath from 'object-path';
import deepMerge from 'deepmerge';
import { applyPatch } from 'fast-json-patch';
import { BehaviorSubject, Subject } from 'rxjs';
import { clone, compilePattern } from '../util';
import { flatten } from 'flat';
var DataState = /** @class */ (function () {
    function DataState(locale, parentDataState) {
        this.locale = locale;
        this.parentDataState = parentDataState;
        this._record = {};
        this._changed = false;
        this._scopes = [];
        this._variables = {};
        this.scopePath = '';
        this.recordChange = new BehaviorSubject(null);
        this.pathChange = new Subject();
        this.validationError = new Subject();
        this.validationReset = new Subject();
        // this.addVariable('$passedData', history.state);
        // this.addVariable('$query', qs.parse(location.search.substring(1), {
        // 	parseArrays: true
        // }));
    }
    Object.defineProperty(DataState.prototype, "changed", {
        get: function () {
            return this._changed;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DataState.prototype, "record", {
        get: function () {
            if (this.parentDataState) {
                return this.parentDataState.get(this.scopePath);
            }
            else {
                return this._record;
            }
        },
        enumerable: true,
        configurable: true
    });
    DataState.prototype.addVariable = function (name, data) {
        this._variables[name] = data;
    };
    DataState.prototype.getVariables = function () {
        return this._variables;
    };
    DataState.prototype.createScope = function (path) {
        var state = new DataState(this.locale, this);
        state.scopePath = path;
        return state;
    };
    DataState.prototype.setRecord = function (record) {
        var _this = this;
        this._record = record;
        this.recordChange.next(record);
        var flatted = flatten(record);
        var changedPaths = Object.keys(flatted);
        changedPaths.forEach(function (cp) { return _this.pathChange.next({
            path: cp,
            currentValue: flatted[cp],
            previousValue: null,
            sender: null,
            containsPath: function (path) {
                return path == cp;
            }
        }); });
    };
    DataState.prototype.markUnchanged = function () {
        this._changed = false;
    };
    DataState.prototype.get = function (path) {
        return objectPath.get(this._record, path);
    };
    DataState.prototype.patch = function (operations) {
        this._record = applyPatch(this._record, operations).newDocument;
        this.recordChange.next(this._record);
    };
    DataState.prototype.getFullPath = function (scopedPath) {
        return "" + (this.scopePath ? this.scopePath + '.' : '') + (scopedPath || '');
    };
    DataState.prototype.mergeWith = function (data) {
        var merged = deepMerge(this._record, data);
        this.recordChange.next(merged);
    };
    DataState.prototype.emitValidationErrors = function (errors) {
        var _this = this;
        errors.forEach(function (error) { return _this.validationError.next(error); });
    };
    DataState.prototype.set = function (path, value, options) {
        if (options === void 0) { options = {}; }
        var previousValue = this.get(path);
        if (!path) {
            return this.setRecord(value);
        }
        else {
            objectPath.set(this._record, path, value);
        }
        if (options) {
            if (options.emitRecordchangeEvent) {
                this.recordChange.next(this._record);
            }
        }
        if (options == null || (options && options.emitEvent !== false)) {
            this.pathChange.next({
                previousValue: previousValue,
                currentValue: value,
                path: path,
                sender: (options ? options.sender : null),
                containsPath: function (pathToCompare) {
                    var re = new RegExp(pathToCompare.replace(/\*/gim, ".*").replace(/\./gim, ".") + '$');
                    var re2 = new RegExp('^' + pathToCompare.replace(/\*/gim, ".*").replace(/\./gim, "."));
                    return re.test(path) || re2.test(path);
                }
            });
        }
    };
    DataState.prototype.compilePattern = function (pattern, variables) {
        if (variables === void 0) { variables = {}; }
        return compilePattern(pattern, __assign(__assign(__assign({}, this._variables), variables), this._record), this.locale);
    };
    DataState.prototype.compileQuery = function (query) {
        var _this = this;
        if (query == null) {
            return query;
        }
        var cloned = clone(query);
        var walk = function (obj) {
            var e_1, _a;
            var keys = Object.keys(obj);
            try {
                for (var keys_1 = __values(keys), keys_1_1 = keys_1.next(); !keys_1_1.done; keys_1_1 = keys_1.next()) {
                    var key = keys_1_1.value;
                    var value = obj[key];
                    if (typeof value === 'object' && value !== null) {
                        walk(value);
                    }
                    else if (Array.isArray(value)) {
                        obj[key] = value.map(function (x) { return _this.compileQuery(x); });
                    }
                    else if (typeof value === 'string') {
                        obj[key] = _this.compilePattern(value);
                    }
                }
            }
            catch (e_1_1) { e_1 = { error: e_1_1 }; }
            finally {
                try {
                    if (keys_1_1 && !keys_1_1.done && (_a = keys_1.return)) _a.call(keys_1);
                }
                finally { if (e_1) throw e_1.error; }
            }
        };
        walk(cloned);
        return cloned;
    };
    DataState.prototype.executeExpression = function (expression) {
        return jexl.evalSync(expression, __assign(__assign({}, this._variables), this._record));
    };
    return DataState;
}());
export { DataState };
//# sourceMappingURL=data-state.js.map